import React, { Component } from 'react'
import styled from 'styled-components'
import { Container, Row, Col, media } from 'styled-bootstrap-grid'
import YouTube from 'react-youtube'
import easing from 'ui/styles/easing'
import InViewMonitor from 'react-inview-monitor'

import PlayIcon from './PlayIcon'

const Wrapper = styled.div`
  margin-bottom: 62px;
  position: relative;
  width: 100%;
  overflow-x: hidden;
  padding: 45px 0 195px;
  transform: ${props => props.inView ? 'translateY(0px)' : 'translateY(30px)'};
  opacity: ${props => props.inView ? 1 : 0};
  transition: transform 0.6s ${easing.easeOutCubic}, opacity 0.6s ${easing.easeOutCubic};

  ${media.xs`
    padding: 30px 0 100px;
    overflow: visible;
  `}
`

const RelContainer = styled(Container)`
  position: relative;
  z-index: 2;
`

const Background = styled.div`
  height: calc(100% - 65px);
  background: ${props => props.theme.colors.theme.light};
  width: calc(91.666667% - 45px);
  right: 0;
  top: 0;
  position: absolute;

  ${media.xs`
    width: 100%;
  `}
`

const OverflowRow = styled(Row)`
  ${media.xs`
    overflow: hidden;
  `}
`

const VideoContainer = styled.div`
  display: flex;
  width: 100%;
  transform: translateX(-${props => props.transformValue}%);
  transition: transform 0.6s ${easing.easeOutCubic};

  > div:first-child {
    margin-left: 0;
  }
`

const VideoCol = styled(Col)`
  ${media.xs`
    flex-shrink: 0;
  `}
`

const Video = styled.div`
  flex: 0 0 100%;
  background: #ccc;
  height: 0;
  padding-top: 56.25%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;

  &:hover {
    cursor: pointer;
    
    svg .hover-path {
     fill: ${props => props.theme.colors.theme.light};
    }
  }
`

const YouTubeContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }

  iframe {
    width: 100%;
    height: 100%;
    padding: 0 10px;
  }
`

const Brush = styled.div`
  width: 45%;
  height: 0;
  padding-bottom: 28%;
  background: url(${props => props.theme.colors.theme.brush1}) center no-repeat / contain;
  position: absolute;
  z-index: 1;
  bottom: 0;
  right: 28%;

  ${media.xs`
    width: calc(100% - 30px);
    padding-bottom: 100%;
    right: auto;
    left: 30px;
    background-size: cover;
    background-position: bottom left;
  `}
`

const Controls = styled.div`
  background: white;
  padding: 45px 35px;
  position: absolute;
  width: 41.666667%;
  right: 50px;
  z-index: 2;
  bottom: 130px;

  ${media.xs`
    padding-top: 65px;
    position: relative;
    width: calc(100% - 40px);
    right: 0;
    bottom: 0;
    margin: 0 auto;
  `}
`

const Buttons = styled.div`
  position: absolute;
  bottom: auto;
  top: 0;
  right: 0;
  display: flex;

  ${media.xs`
    bottom: 0;
    top: auto;
  `}
`

const Button = styled.button`
  background: ${props => props.theme.colors.theme.base};
  height: 40px;
  width: 80px;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:first-child {
    border-right: 1px solid ${props => props.theme.colors.theme.dark};

    svg {
      transform: rotate(180deg);
    }

    &:hover svg {
      transform: rotate(180deg) translateX(25%);
    }
  }

  svg {
    transition: transform 0.3s ${easing.easeOutCubic};
    
    path {
      fill: ${props => props.disabled ? props.theme.colors.theme.dark : props.theme.colors.grey.lightest};
      transition: fill 0.3s ${easing.easeOutCubic};
    }
  }

  &:hover {
    cursor: pointer;

    svg {
      transform: translateX(25%);
    }
  }
`

const Title = styled.h2`
  font-size: 32px;
  color: ${props => props.theme.colors.theme.base};
  margin: 0 0 25px;
`

const Description = styled.p`
  font-size: 16px;
  line-height: 21px;
  color: ${props => props.theme.colors.grey.darkest};
  margin: 0 0 25px;
  height: 65px;
`

const Counter = styled.p`
  font-family: 'Hill', sans-serif;
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 0;
  color: ${props => props.theme.colors.theme.base};
`

// Options
const videoOptions = {
  playerVars: {
    autoplay: true
  }
}


export default class Videos extends Component {
  state = {
    activeSlide: 0,
    slideTransformValue: 0,
    transformValue: 0,
  }

  getSlideTransformValue() {
    const pixelWidth = window.innerWidth * (0.66666667 + 0.08333333)
    const percentage = window.innerWidth <= 575 ? 100 : (pixelWidth * 100) / window.innerWidth

    return this.setState({slideTransformValue: percentage})
  }

  nextSlide() {
    if(this.state.activeSlide !== this.props.videos.length - 1) {
      const nextSlide = this.state.activeSlide + 1
      
      this.setState({
        videoPlaying: false,
        activeSlide: nextSlide,
        transformValue: nextSlide * this.state.slideTransformValue
      })
    }
  }

  prevSlide() {
    if(this.state.activeSlide) {
      const prevSlide = this.state.activeSlide - 1
      
      this.setState({
        videoPlaying: false,
        activeSlide: prevSlide,
        transformValue: prevSlide * this.state.slideTransformValue
      })
    }
  }

  playVideo() {
    this.setState({videoPlaying: this.state.activeSlide})
  }

  componentDidMount() {
    this.getSlideTransformValue()
  }
  
  render() {
    const { videos } = this.props

    const {
      videoPlaying,
      activeSlide
    } = this.state

    return (
      <InViewMonitor childPropsInView={{inView: true}}>
        <Wrapper>
        
          <RelContainer fluid>
            <OverflowRow>
              <VideoContainer transformValue={this.state.transformValue}>
                {videos.map((video, index) => {
                  return(
                    <VideoCol
                      sm={8}
                      smOffset={1}
                      key={video.video}
                    >
                      <Video
                        image={video.image.sourceUrl}
                        onClick={() => this.playVideo()}
                      >
                        <PlayIcon />
                        {videoPlaying === index &&
                          <YouTubeContainer>
                            <YouTube
                              videoId={video.video}
                              opts={videoOptions}
                            />
                          </YouTubeContainer>
                        }
                      </Video>
                    </VideoCol>
                  )
                })}
              </VideoContainer>
            </OverflowRow>
          </RelContainer>

          <Controls>
            
            <Buttons>
              <Button
                onClick={() => this.prevSlide()}
                disabled={!activeSlide}
              >
                <svg
                  width="19"
                  height="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.89891421 14.873342c-.15886232.1491125-.28188712.1887755-.48300577.1887755h-.86197469c-.36078327 0-.44315634-.2424962-.20085135-.4699303l6.84525125-6.4251412H.4011678c-.2607587 0-.4011673-.1317913-.4011673-.3765467v-.5188812c0-.2447554.1404086-.3765467.4011673-.3765467h15.79716585L9.3530824.4699302C9.11077741.2424961 9.19315048-1e-7 9.55420105-1e-7h.86197469c.20111865 0 .3417945.056482.48273806.1887755l7.5411429 7.0783243c.18106029.1699482.18106029.3579706 0 .5279188l-7.5411429 7.0783242 4.1e-7-7e-7z"
                  />
                </svg>
              </Button>
              <Button
                onClick={() => this.nextSlide()}
                disabled={activeSlide === videos.length - 1}
              >
                <svg
                  width="19"
                  height="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.89891421 14.873342c-.15886232.1491125-.28188712.1887755-.48300577.1887755h-.86197469c-.36078327 0-.44315634-.2424962-.20085135-.4699303l6.84525125-6.4251412H.4011678c-.2607587 0-.4011673-.1317913-.4011673-.3765467v-.5188812c0-.2447554.1404086-.3765467.4011673-.3765467h15.79716585L9.3530824.4699302C9.11077741.2424961 9.19315048-1e-7 9.55420105-1e-7h.86197469c.20111865 0 .3417945.056482.48273806.1887755l7.5411429 7.0783243c.18106029.1699482.18106029.3579706 0 .5279188l-7.5411429 7.0783242 4.1e-7-7e-7z"
                  />
                </svg>
              </Button>
            </Buttons>
            
            <Title>{videos[activeSlide].title}</Title>
            
            {videos[activeSlide].description &&
              <Description>{videos[activeSlide].description}</Description>
            }

            <Counter>
              {activeSlide + 1 < 10 ? `0${activeSlide + 1}` : activeSlide + 1}
              /
              {videos.length < 10 ? `0${videos.length}` : videos.length}
            </Counter>
          
          </Controls>

          <Brush />
          
          <Background />
        
        </Wrapper>
      </InViewMonitor>
    )
  }
}



// WEBPACK FOOTER //
// ./src/ui/components/modules/Videos/index.js